import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import type { HorarioAtencion } from '../types/portal'

/**
 * Horario de atención y feriados — cuándo corre el reloj del asesor.
 *
 * Documento de QA «Asignación de conversaciones» (20-oct-2026): fuera de las
 * franjas el reloj del asesor está en pausa —no vence, no reasigna, no avisa—
 * y el cliente que escribe lee el horario (M3). Lo mantiene supervisión desde
 * acá para que un feriado nuevo o un cambio de horario no necesite despliegue.
 *
 * El backend valida antes de guardar (`horario.validar`): un horario roto
 * vuelve con el motivo, en vez de caer en silencio al horario por defecto.
 *
 * Cada día tiene hasta dos franjas —mañana y tarde—, que es como trabaja el
 * equipo: el refrigerio es el hueco entre las dos.
 */

const DIAS: [string, string][] = [
  ['lun', 'Lunes'], ['mar', 'Martes'], ['mie', 'Miércoles'], ['jue', 'Jueves'],
  ['vie', 'Viernes'], ['sab', 'Sábado'], ['dom', 'Domingo'],
]

type Franja = [string, string]

interface Respuesta {
  horario: HorarioAtencion
  por_defecto: boolean
  en_palabras: string
  updated_by: string | null
  updated_at: string | null
}

/** Las dos franjas del día, con la que falta en blanco. */
function dosFranjas(franjas: Franja[] | undefined): [Franja | null, Franja | null] {
  return [franjas?.[0] ?? null, franjas?.[1] ?? null]
}

export default function HorarioDeAtencion() {
  const [horario, setHorario] = useState<HorarioAtencion | null>(null)
  const [enPalabras, setEnPalabras] = useState('')
  const [porDefecto, setPorDefecto] = useState(false)
  const [quien, setQuien] = useState<string | null>(null)
  const [nuevoFeriado, setNuevoFeriado] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    try {
      const res = await apiClient.get<Respuesta>('/horario-atencion')
      setHorario(res.data.horario)
      setEnPalabras(res.data.en_palabras)
      setPorDefecto(res.data.por_defecto)
      setQuien(res.data.updated_by
        ? `${res.data.updated_by}${res.data.updated_at
          ? ` · ${new Date(res.data.updated_at).toLocaleString('es-PE')}` : ''}`
        : null)
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'No se pudo leer el horario de atención.'))
    }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  function cambiarFranja(dia: string, i: 0 | 1, valor: Franja | null) {
    if (!horario) return
    const [a, b] = dosFranjas(horario.franjas[dia])
    const nuevas = i === 0 ? [valor, b] : [a, valor]
    setHorario({
      ...horario,
      franjas: { ...horario.franjas, [dia]: nuevas.filter((f): f is Franja => f !== null) },
    })
    setAviso(null)
  }

  function agregarFeriado() {
    if (!horario || !nuevoFeriado || horario.feriados.includes(nuevoFeriado)) return
    setHorario({ ...horario, feriados: [...horario.feriados, nuevoFeriado].sort() })
    setNuevoFeriado('')
    setAviso(null)
  }

  function quitarFeriado(fecha: string) {
    if (!horario) return
    setHorario({ ...horario, feriados: horario.feriados.filter((f) => f !== fecha) })
    setAviso(null)
  }

  async function guardar() {
    if (!horario) return
    setGuardando(true)
    try {
      const res = await apiClient.put<{ horario: HorarioAtencion; en_palabras: string }>(
        '/horario-atencion', { horario })
      setHorario(res.data.horario)
      setEnPalabras(res.data.en_palabras)
      setPorDefecto(false)
      setError(null)
      setAviso('Guardado. El reloj del asesor usa el horario nuevo en menos de un minuto.')
      void cargar()
    } catch (err) {
      setError(errorMessage(err, 'No se pudo guardar el horario.'))
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="card flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-card font-bold">Horario de atención</h2>
        <p className="mt-1 text-body text-ink-muted">
          Fuera de estas franjas el reloj del asesor está en pausa: el plazo de 10 minutos
          no corre y no se reasigna ninguna conversación. Los feriados cierran el día entero.
          {porDefecto && ' Hoy rige el horario por defecto: nadie lo guardó todavía.'}
        </p>
        {enPalabras && (
          <p className="mt-1 text-body">
            El cliente que escribe fuera de horario lee: «Nuestro horario de atención es{' '}
            <b>{enPalabras}</b> Te responderemos dentro de ese horario.»
          </p>
        )}
      </div>

      {error && <p className="rounded-card border border-danger-border bg-danger-light px-3 py-2 text-body text-danger">{error}</p>}
      {aviso && <p className="rounded-card bg-brand-light px-3 py-2 text-body text-brand">{aviso}</p>}

      {horario && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-body">
              <thead>
                <tr className="text-left text-ink-muted">
                  <th className="py-1 pr-3 font-semibold">Día</th>
                  <th className="py-1 pr-3 font-semibold">Mañana</th>
                  <th className="py-1 font-semibold">Tarde</th>
                </tr>
              </thead>
              <tbody>
                {DIAS.map(([dia, nombre]) => {
                  const franjas = dosFranjas(horario.franjas[dia])
                  return (
                    <tr key={dia} className="border-t border-surface-border">
                      <td className="py-1.5 pr-3 font-semibold">{nombre}</td>
                      {([0, 1] as const).map((i) => {
                        const f = franjas[i]
                        const porDefectoDeLaFranja: Franja = i === 0 ? ['09:00', '13:00'] : ['14:00', '18:00']
                        return (
                          <td key={i} className="py-1.5 pr-3">
                            <label className="flex flex-wrap items-center gap-1.5">
                              <input
                                type="checkbox"
                                checked={f !== null}
                                onChange={(e) => cambiarFranja(dia, i, e.target.checked ? porDefectoDeLaFranja : null)}
                                aria-label={`${nombre}, ${i === 0 ? 'mañana' : 'tarde'}`}
                              />
                              {f ? (
                                <>
                                  <input type="time" className="input py-0.5" value={f[0]}
                                         onChange={(e) => cambiarFranja(dia, i, [e.target.value, f[1]])} />
                                  <span className="text-ink-muted">a</span>
                                  <input type="time" className="input py-0.5" value={f[1]}
                                         onChange={(e) => cambiarFranja(dia, i, [f[0], e.target.value])} />
                                </>
                              ) : (
                                <span className="text-ink-faint">Cerrado</span>
                              )}
                            </label>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="font-semibold text-body">Feriados</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {horario.feriados.length === 0 && <span className="text-body text-ink-faint">Ninguno cargado.</span>}
              {horario.feriados.map((f) => (
                <span key={f} className="badge-gray inline-flex items-center gap-1">
                  {new Date(`${f}T12:00:00`).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                  <button type="button" className="text-ink-muted hover:text-danger" onClick={() => quitarFeriado(f)}
                          aria-label={`Quitar el feriado ${f}`}>✕</button>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <input type="date" className="input" value={nuevoFeriado}
                     onChange={(e) => setNuevoFeriado(e.target.value)} />
              <button type="button" className="btn-ghost" onClick={agregarFeriado} disabled={!nuevoFeriado}>
                Agregar feriado
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn-primary" onClick={() => void guardar()} disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar horario'}
            </button>
            {quien && <span className="text-micro text-ink-muted">Última modificación: {quien}</span>}
          </div>
        </>
      )}
    </section>
  )
}
